import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LifeBuoy, Search, Sparkles, MessageSquare, Send, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import ShopHeader from "@/components/ShopHeader";

const CAT_ICON = "📦";

export default function HelpCenter() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [data, setData] = useState({ articles: [], categories: [] });
  const [article, setArticle] = useState(null);
  const [aiQ, setAiQ] = useState("");
  const [aiA, setAiA] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = () => api.get("/support/kb", { params: { q: q || undefined, category: cat } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);
  const [sp] = useSearchParams();
  useEffect(() => { const s = sp.get("article"); if (s) openArticle(s); /* eslint-disable-next-line */ }, [sp]);

  const openArticle = (slug) => api.get(`/support/kb/${slug}`).then((r) => setArticle(r.data.article)).catch(() => {});
  const askAI = async () => {
    if (!aiQ.trim()) return;
    setAiLoading(true); setAiA(null);
    try { const r = await api.post("/support/ai/ask", { question: aiQ }); setAiA(r.data); }
    catch (e) {
      if (e.response?.status === 401) { nav("/login"); return; }
      setAiA({ answer: "Something went wrong. Please contact support.", verified: false, escalate_suggested: true });
    }
    finally { setAiLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <ShopHeader />
      <header className="brand-gradient-bg px-6 py-14 text-center text-white">
        <div className="mx-auto flex max-w-3xl flex-col items-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold"><LifeBuoy className="h-4 w-4" /> Suffle Online Help Center</div>
          <h1 className="text-3xl font-extrabold sm:text-4xl">How can we help?</h1>
          <p className="mt-2 text-sm text-white/80">Shop. Discover. Suffle. · AI-Powered Marketplace</p>
          <div className="mt-6 flex w-full max-w-xl items-center gap-2 rounded-full bg-white p-1.5 shadow-lg">
            <Search className="ml-3 h-5 w-5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="help-search" placeholder="Search articles…" className="flex-1 bg-transparent px-2 py-2 text-sm text-slate-800 outline-none" />
            <button onClick={load} className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">Search</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {article ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="help-article">
            <button onClick={() => setArticle(null)} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500"><ArrowLeft className="h-4 w-4" /> Back to Help Center</button>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">{article.category}</p>
            <h2 className="mt-1 text-2xl font-extrabold text-slate-900">{article.title}</h2>
            <div className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{article.body}</div>
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap gap-2" data-testid="help-categories">
              <button onClick={() => setCat("all")} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${cat === "all" ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>All</button>
              {data.categories.map((c) => (
                <button key={c} onClick={() => setCat(c)} data-testid={`help-cat-${c}`} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${cat === c ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{c}</button>
              ))}
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-2">
                {data.articles.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">No help articles yet. Try “Ask Suffle AI” or contact support.</div>
                ) : (
                  <div className="space-y-3">
                    {data.articles.map((a) => (
                      <button key={a.slug} onClick={() => openArticle(a.slug)} data-testid={`help-art-${a.slug}`} className="block w-full rounded-2xl border border-slate-200 bg-white p-4 text-left transition hover:shadow-md">
                        <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">{CAT_ICON} {a.category}</p>
                        <p className="mt-1 font-bold text-slate-800">{a.title}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <aside className="space-y-4">
                <div className="rounded-2xl border border-indigo-100 bg-white p-4" data-testid="ask-ai-box">
                  <p className="flex items-center gap-1.5 text-sm font-bold text-slate-800"><Sparkles className="h-4 w-4 text-indigo-500" /> Ask Suffle AI</p>
                  <p className="mt-1 text-xs text-slate-500">Where is my order? How do I return this? AI answers from your real account data.</p>
                  <textarea value={aiQ} onChange={(e) => setAiQ(e.target.value)} data-testid="ask-ai-input" rows={2} className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" placeholder="Type your question…" />
                  <button onClick={askAI} disabled={aiLoading} data-testid="ask-ai-btn" className="mt-2 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"><Send className="h-4 w-4" /> {aiLoading ? "Thinking…" : "Ask"}</button>
                  {aiA && (
                    <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="ask-ai-answer">
                      {aiA.answer}
                      {aiA.escalate_suggested && <button onClick={() => nav("/app/support")} className="mt-2 block text-xs font-semibold text-indigo-600 underline">Contact support instead →</button>}
                    </div>
                  )}
                </div>
                <button onClick={() => nav("/app/support")} data-testid="contact-support-btn" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700 transition hover:shadow-md"><MessageSquare className="h-4 w-4" /> Contact Support</button>
              </aside>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
